import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FaCogs } from 'react-icons/fa';
import { IoMdPie } from 'react-icons/io';
import { PropagateLoader } from 'react-spinners';
import { v4 as uuidv4 } from 'uuid';

import BreadCrumb from '~/components/BreadCrumb';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import TableCurrencyInput from '~/components/TableInputs/Currency';
import TableDefaultInput from '~/components/TableInputs/Default';
import Toggle from '~/components/Toggle';
import SaveButton from '~/components/Buttons/Save';

import { getCurrencyFormatValue, getFormattedCurrency } from '~/utils';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Table, Td } from './styles';

interface IGroup {
  id: string;
  name: string;
  total_price: number;
  used_price: number;
  temp_id: string;
}

const Record: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<{ id: string; code: string; open: boolean }>();
  const [open, setOpen] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadGroups = async () => {
      await api.get(`/quarters/balances/${state.id}`).then(response => {
        setOpen(response.data.open);

        setGroups(
          response.data.rebate_balances.map(
            (rebate_balance: {
              total_price: number;
              used_price: number;
              policy_group: { id: string; name: string };
            }) => ({
              id: rebate_balance.policy_group.id,
              name: rebate_balance.policy_group.name,
              total_price: rebate_balance.total_price,
              used_price: rebate_balance.used_price,
              temp_id: uuidv4(),
            }),
          ),
        );

        setLoadingGroups(false);
      });
    };

    if (state && loadingGroups) {
      loadGroups();
    }
  }, [loadingGroups, state]);

  const submitForm = useCallback(async () => {
    try {
      setIsSaving(true);

      await api.put(`/quarters`, {
        id: state.id,
        open,
        groups,
      });

      addToast({
        type: 'success',
        title: 'Trimestre atualizado!',
        description: 'Trimestre atualizado com sucesso.',
      });

      history.push('/configurations/rebatebalancequarters');
    } catch (error: any) {
      setIsSaving(false);

      addToast({
        type: 'error',
        title: 'Ocorreu um erro!',
        description: error.response.data.message,
      });
    }
  }, [addToast, groups, history, open, state]);

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChildPath="/configurations/rebatebalancequarters"
        firstChild="Saldos Rebate - Trimestres"
        secondChild="Edição de Trimestre"
        mainIcon={FaCogs}
        firstChildIcon={IoMdPie}
        secondChildIcon={IoMdPie}
        mainColored
        firstChildColored
      />

      <form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ width: 400 }}>
            <DefaultInput
              name="code"
              labelFor="code"
              labelText="Código"
              type="text"
              defaultValue={state?.code}
              disabled
            />
          </div>

          <div>
            <Toggle label="Aberto" onChange={e => setOpen(e)} checked={open} />
          </div>
        </div>

        <div
          style={{ marginTop: 30, display: 'flex', justifyContent: 'center' }}
        >
          <Table>
            <thead>
              <tr>
                <th colSpan={4} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontStyle: 'italic' }}>
                    Grupos
                  </div>
                </th>
              </tr>
              <tr>
                <th style={{ width: '25%', textAlign: 'left' }}>Nome</th>
                <th style={{ width: '25%', textAlign: 'center' }}>
                  Valor Total
                </th>
                <th style={{ width: '25%', textAlign: 'center' }}>
                  Valor Utilizado
                </th>
                <th style={{ width: '25%', textAlign: 'center' }}>
                  Valor Pendente
                </th>
              </tr>
            </thead>

            <tbody>
              {loadingGroups ? (
                <tr>
                  <Td
                    colSpan={4}
                    style={{ padding: '3rem', textAlign: 'center' }}
                  >
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              ) : groups.length > 0 ? (
                groups
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(group => (
                    <tr key={group.temp_id}>
                      <Td
                        style={{ width: '25%', textAlign: 'left' }}
                        heading="NOME"
                      >
                        {group.name}
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'center' }}
                        heading="VALOR TOTAL"
                      >
                        <TableCurrencyInput
                          type="text"
                          labelText=""
                          upperCaseLabel
                          defaultValue={group.total_price}
                          onSet={e => {
                            setGroups(prevGroups =>
                              prevGroups.map(prevGroup =>
                                prevGroup.temp_id === group.temp_id
                                  ? {
                                      ...prevGroup,
                                      total_price: getCurrencyFormatValue(
                                        e.target.value,
                                      ),
                                    }
                                  : { ...prevGroup },
                              ),
                            );
                          }}
                        />
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'center' }}
                        heading="VALOR UTILIZADO"
                      >
                        <TableDefaultInput
                          type="text"
                          labelText=""
                          upperCaseLabel
                          value={getFormattedCurrency(group.used_price)}
                          disabled
                        />
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'center' }}
                        heading="VALOR PENDENTE"
                      >
                        <TableDefaultInput
                          type="text"
                          labelText=""
                          upperCaseLabel
                          value={getFormattedCurrency(
                            group.total_price - group.used_price,
                          )}
                          disabled
                        />
                      </Td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <Td colSpan={4} style={{ textAlign: 'center' }}>
                    Nenhum grupo disponível
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div
          style={{
            marginTop: 50,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => submitForm()}
        title="Confirma gravação do trimestre?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={() => history.push('/configurations/rebatebalancequarters')}
      />
    </Container>
  );
};

export default Record;
